import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule, Provider } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import {
    ActionUtilitiesInstanceToken,
    ActionUtilitiesService,
    FormsWriteOnlineOnlyService,
    PaceForms2Module,
} from '@evorait/pace-forms';
import { PaceOfflineModule } from '@evorait/pace-offline';
import { APP_VERSION } from '../environments/appversion';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { appRoutes } from './app.routes';
import { InitializationService } from './services/initialization/initialization.service';
import { PaceSideMenuService } from './services/pace-side-menu/pace-side-menu.service';
import { OdataConfigService } from './services/odata-config/odata-config.service';
import { OdataValueHelperService } from './services/odata-value-helper/odata-value-helper.service';
import { ConfigInterfaceService } from './services/config-interface/config-interface.service';
import {
    HttpOnlineInterceptor,
    MockModeInterceptorService,
    PaceCoreModule,
    TranslationService,
} from '@evorait/pace-core';
import { NotificationService } from './services/notification/notification.service';
import { SynchronizationService } from './services/synchronization/synchronization.service';
import { HttpOnlineAuthorsInterceptor } from './services/interceptor/http-online-authors-interceptor';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ModalModule } from 'ngx-bootstrap/modal';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { LetDirective, PushPipe } from '@ngrx/component';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { HeaderComponent } from './components/header/header.component';
import { MainComponent } from './components/main/main.component';
import { HomePageComponent } from './components/home-page/home-page.component';
import { DropdownComponent } from './components/dropdown/dropdown.component';
import { AuthorsEffectsService } from './store/effects/authors-effects.service';
import { AuthorsReducer } from './store/reducers/authors.reducer';
import { PaceMsalAuthenticationModule, PaceMsalAuthService } from '@evorait/pace-authentication';
import { map, Observable, of, take } from 'rxjs';
import {
    AngularPlugin,
    ApplicationinsightsAngularpluginErrorService,
} from '@microsoft/applicationinsights-angularplugin-js';

export function appInitializerFactory(msalAuthService?: PaceMsalAuthService): () => Observable<void> {
    const authConfigured = !!environment.msal?.clientId;

    if (msalAuthService && authConfigured) {
        return (): Observable<void> =>
            msalAuthService.ready$().pipe(
                map(() => undefined),
                take(1),
            );
    } else {
        return () => of(undefined);
    }
}

const interceptorProviders: Provider[] = [
    // http interceptor for intercepting authentication requests
    {
        provide: HTTP_INTERCEPTORS,
        useClass: HttpOnlineInterceptor,
        multi: true,
    },
    // http interceptor for adding header params
    {
        provide: HTTP_INTERCEPTORS,
        useClass: HttpOnlineAuthorsInterceptor,
        multi: true,
    },
];

if (environment.operateInMockMode) {
    interceptorProviders.push({
        provide: HTTP_INTERCEPTORS,
        useClass: MockModeInterceptorService,
        multi: true,
    });
}

const authModule = environment.msal ? [PaceMsalAuthenticationModule.forRoot()] : [];

@NgModule({
    declarations: [AppComponent, HeaderComponent, MainComponent, HomePageComponent, DropdownComponent],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        FontAwesomeModule,
        ProgressbarModule.forRoot(),
        PopoverModule.forRoot(),
        FormsModule,
        ReactiveFormsModule,
        ModalModule.forRoot(),
        LoggerModule.forRoot({ level: NgxLoggerLevel.DEBUG }),
        RouterModule.forRoot(appRoutes, {
            initialNavigation: 'enabledNonBlocking',
            enableTracing: false,
            useHash: true,
        }),
        PaceForms2Module.forRoot({
            notificationService: NotificationService,
            formsWriteService: FormsWriteOnlineOnlyService,
            translationMessages: {
                en: {
                    FORM_SUBMISSION_ERROR: 'Submit was not successful',
                    FORM_SUBMISSION_ERROR_BODY: 'Please refresh the web page, to guarantee your network authentication',
                },
                de: {
                    FORM_SUBMISSION_ERROR: 'Der Submit war nicht erfolgreich',
                    FORM_SUBMISSION_ERROR_BODY:
                        'Bitte laden sie die Seite erneut, um die Netzwerkauthentifizierung zu gewähren',
                },
            },
        }),
        PaceCoreModule.forRoot({
            initializationService: InitializationService,
            odataConfigService: OdataConfigService,
            odataValueHelper: OdataValueHelperService,
            sideMenuService: PaceSideMenuService,
            synchronizationService: SynchronizationService,
            configInterfaceService: ConfigInterfaceService,
            environment: environment,
            appVersion: APP_VERSION,
        }),
        StoreModule.forRoot(
            { authorsState: AuthorsReducer },
            {
                runtimeChecks: {
                    strictStateImmutability: true,
                    strictActionImmutability: true,
                    strictStateSerializability: true,
                    strictActionSerializability: true,
                    strictActionWithinNgZone: true,
                    strictActionTypeUniqueness: true,
                },
            },
        ),
        EffectsModule.forRoot([AuthorsEffectsService]),
        PaceOfflineModule.forRoot([]),
        TranslateModule.forRoot(),
        TooltipModule.forRoot(),
        LetDirective,
        PushPipe,
        ...authModule,
    ],
    providers: [
        provideHttpClient(withInterceptorsFromDi()),
        ...interceptorProviders,
        {
            provide: ActionUtilitiesInstanceToken,
            useExisting: ActionUtilitiesService,
        },
        {
            provide: APP_INITIALIZER, // TODO Angular ^19: deprecated with angular v19. change to provideAngularInitializer().
            useFactory: appInitializerFactory,
            deps: environment.msal ? [PaceMsalAuthService] : [],
            multi: true,
        },
        { provide: AngularPlugin, useValue: new AngularPlugin() },
        { provide: ErrorHandler, useClass: ApplicationinsightsAngularpluginErrorService },
    ],
})
export class AppModule {
    public constructor(
        private readonly translationService: TranslationService,
        private readonly translate: TranslateService,
    ) {
        // FI should only be available in english
        const lang = translationService.defaultLanguageKey;
        // eslint-disable-next-line @typescript-eslint/no-var-requires,@typescript-eslint/no-require-imports
        const defaultTranslation = <{ [idx: string]: string }>require(`../assets/i18n/${lang}.json`);
        this.translate.setTranslation(translationService.defaultLanguageKey, defaultTranslation, true);

        this.translationService.setLanguageKey(lang);
    }
}
